import { useEffect, useState } from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { Search, ShoppingBag, User, Menu, X, Heart, LogOut } from "lucide-react";
import { Logo } from "../Logo";
import { useAuth, useCart, useLang } from "../../lib/ctx";
import { api, img } from "../../lib/api";

export const NAV = [["shop", "/shop"], ["vintage", "/shop/vintage"], ["topup", "/game-top-up"]];
const NAV_END = [["newArrivals", "/new-arrivals"], ["offers", "/special-offers"], ["blog", "/blog"]];
export const subUrl = (slug, sub) => `/shop/${slug}${sub ? `?sub=${encodeURIComponent(sub)}` : ""}`;

export const useCategories = () => {
  const [cats, setCats] = useState([]);
  useEffect(() => {
    api.get("/catalog/meta").then((r) => {
      const pop = Object.fromEntries((r.data.popular_categories || []).map((p, i) => [p.slug, [p.img || "", i]]));
      setCats(r.data.categories.filter((c) => c.slug !== "vintage").map((c) => (c.slug in pop ? { ...c, popular: true, popular_img: pop[c.slug][0], popular_order: pop[c.slug][1] } : c)));
    }).catch(() => setCats([]));
  }, []);
  return cats;
};

const PopularRow = ({ cats, lang, close }) => {
  const list = cats.filter((c) => c.popular).sort((a, b) => a.popular_order - b.popular_order);
  if (!list.length) return null;
  return (
    <div className="mb-5 border-b border-[#eee] pb-5" data-testid="menu-popular">
      <p className="mb-3 text-[11px] font-bold uppercase tracking-[0.2em] text-[#ee4d2d]">{lang === "bm" ? "Kategori Popular" : "Popular Categories"}</p>
      <div className="grid grid-cols-4 gap-2 sm:gap-3 lg:grid-cols-8">
        {list.map((c) => (
          <Link key={c.slug} to={subUrl(c.slug)} onClick={close} className="group relative overflow-hidden rounded-lg border border-[#eee] bg-white" data-testid={`menu-popular-${c.slug}`}>
            {c.popular_img ? <img src={img(c.popular_img)} alt={c.name} className="aspect-square w-full object-cover transition-transform duration-300 group-hover:scale-105" /> : <div className="aspect-square w-full bg-[#fff1ee]" />}
            <span className="absolute left-1 top-1 rounded-sm bg-[#ee4d2d] px-1 py-px text-[8px] font-bold uppercase text-white sm:left-1.5 sm:top-1.5 sm:px-1.5 sm:py-0.5 sm:text-[9px]">Popular</span>
            <span className="block truncate px-1.5 py-1 text-[10px] font-semibold text-[#222] group-hover:text-[#ee4d2d] sm:px-2 sm:py-1.5 sm:text-xs">{lang === "bm" ? c.name_bm : c.name}</span>
          </Link>
        ))}
      </div>
    </div>
  );
};

const CatBlock = ({ c, lang, close }) => (
  <div className="break-inside-avoid pb-4" data-testid={`nav-cat-${c.slug}`}>
    <Link to={subUrl(c.slug)} onClick={close} className="inline-flex items-center gap-1.5 text-sm font-bold text-[#222] transition-colors duration-150 hover:text-[#ee4d2d]">{lang === "bm" ? c.name_bm : c.name}{c.popular && <span className="rounded-sm bg-[#ee4d2d] px-1 py-px text-[8px] font-bold uppercase text-white" data-testid={`nav-popular-badge-${c.slug}`}>Popular</span>}</Link>
    {c.subs.length > 0 && (
      <ul className="mt-1 space-y-0.5" data-testid={`nav-sub-menu-${c.slug}`}>
        {c.subs.map((s) => <li key={s}><Link to={subUrl(c.slug, s)} onClick={close} className="text-xs text-[#666] transition-colors duration-150 hover:text-[#ee4d2d]" data-testid={`nav-sub-${c.slug}-${s.replace(/\W+/g, "-").toLowerCase()}`}>{s}</Link></li>)}
      </ul>
    )}
  </div>
);

const LangSwitch = () => {
  const { lang, setLang } = useLang();
  return (
    <div className="flex rounded-sm border border-white/60 font-mono text-[11px]" data-testid="lang-switcher">
      {["en", "bm"].map((l) => (
        <button key={l} onClick={() => setLang(l)} data-testid={`lang-${l}-btn`}
          className={`px-2 py-1 uppercase transition-colors duration-200 ${lang === l ? "bg-white text-[#ee4d2d]" : "text-white/90"}`}>{l}</button>
      ))}
    </div>
  );
};

export const Header = () => {
  const { t, lang } = useLang();
  const cats = useCategories();
  const [q, setQ] = useState("");
  const { user, logout } = useAuth();
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const nav = useNavigate();
  useEffect(() => {
    if (!menu) { return undefined; }
    const esc = (e) => { if (e.key === "Escape") setMenu(false); };
    window.addEventListener("keydown", esc);
    return () => { window.removeEventListener("keydown", esc); };
  }, [menu]);
  const submit = (e) => {
    e.preventDefault();
    nav(`/shop?q=${encodeURIComponent(q)}`);
    setMenu(false);
  };
  return (
    <header className="keep-orange sticky top-0 z-50 shadow-[0_1px_4px_rgba(0,0,0,.15)]">
      <div className="sp-header">
      <div className="mx-auto flex max-w-7xl items-center justify-between px-4 pt-1.5 text-[11px] text-white/90 sm:px-8 lg:px-12"><span className="hidden sm:block" data-testid="header-slogan">{t("slogan")}</span><a href="https://wa.me/60123655966" target="_blank" rel="noreferrer" className="hover:text-white" data-testid="header-whatsapp-link">WhatsApp +60 12-365 5966</a></div>
      <div className="mx-auto flex max-w-7xl items-center gap-4 px-4 py-3 text-white sm:px-8 lg:px-12">
        <button className="flex items-center gap-2 rounded-sm px-1 py-1 transition-colors duration-200 hover:bg-white/10" onClick={() => setMenu(!menu)} data-testid="mobile-menu-btn" aria-label="Menu" aria-expanded={menu}>{menu ? <X /> : <Menu />}<span className="hidden text-sm font-semibold lg:inline">{lang === "bm" ? "Kategori" : "Categories"}</span></button>
        <Link to="/" data-testid="home-link" className="rounded-full bg-white/10 p-0.5"><Logo /></Link>
        <form onSubmit={submit} className="ml-auto hidden max-w-2xl flex-1 items-center rounded-sm bg-white p-1 md:flex">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="w-full bg-transparent px-3 py-1.5 text-sm text-[#222] outline-none" data-testid="header-search-input" />
          <button className="rounded-sm bg-[#fb5533] px-5 py-2 text-white transition-colors duration-200 hover:bg-[#e0401f]" aria-label="search" data-testid="header-search-btn"><Search className="h-4 w-4" /></button>
        </form>
        <div className="ml-auto flex items-center gap-3 sm:gap-4 md:ml-0">
          <LangSwitch />
          {user && <Link to="/account/wishlist" aria-label="wishlist" className="hidden sm:block" data-testid="header-wishlist-link"><Heart className="h-5 w-5" /></Link>}
          <Link to={user ? (["admin", "staff"].includes(user.role) ? "/admin" : "/account") : "/login"} data-testid="header-account-link" aria-label="account"><User className="h-5 w-5" /></Link>
          {user && <button onClick={async () => { await logout(); nav("/"); }} aria-label={t("logout")} title={t("logout")} className="hidden sm:block" data-testid="header-logout-btn"><LogOut className="h-5 w-5" /></button>}
          <button onClick={() => setOpen(true)} className="relative" data-testid="header-cart-btn" aria-label="cart">
            <ShoppingBag className="h-6 w-6" />
            {count > 0 && <span className="absolute -right-2 -top-2 grid h-4 min-w-4 place-items-center rounded-full border border-[#ee4d2d] bg-white px-1 text-[10px] font-bold text-[#ee4d2d]" data-testid="cart-count">{count}</span>}
          </button>
        </div>
      </div>
      </div>
      {menu && <button aria-label="Close menu" onClick={() => setMenu(false)} className="fixed inset-0 top-[var(--hdr,0px)] -z-10 cursor-default bg-black/30" data-testid="menu-overlay" />}
      <nav className={`${menu ? "block" : "hidden"} border-b border-[#eee] bg-white shadow-lg`} data-testid="category-menu">
        <form onSubmit={submit} className="flex border-b border-white/10 px-4 py-2 md:hidden">
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder={t("search")} className="field" data-testid="mobile-search-input" />
        </form>
        <div className="mx-auto max-h-[75vh] max-w-7xl overflow-y-auto px-4 py-4 sm:px-8 lg:px-12">
          <ul className="mb-4 flex flex-wrap gap-2 border-b border-[#eee] pb-4">
            {[...NAV, ...NAV_END].map(([k, to]) => (
              <li key={k}><NavLink to={to} end={to === "/shop"} onClick={() => setMenu(false)} data-testid={`nav-${k}`} className={({ isActive }) => `block rounded-full border px-3 py-1 text-xs font-semibold transition-colors duration-200 ${isActive ? "border-[#ee4d2d] bg-[#ee4d2d] text-white" : "border-[#e5e5e5] text-[#333] hover:border-[#ee4d2d] hover:text-[#ee4d2d]"}`}>{t(k)}</NavLink></li>
            ))}
            {user && <li className="sm:hidden"><button onClick={async () => { setMenu(false); await logout(); nav("/"); }} className="flex items-center gap-1 rounded-full border border-red-200 px-3 py-1 text-xs font-semibold text-red-500" data-testid="mobile-logout-btn"><LogOut className="h-3 w-3" />{t("logout")}</button></li>}
          </ul>
          <PopularRow cats={cats} lang={lang} close={() => setMenu(false)} />
          <div className="columns-1 gap-8 sm:columns-2 lg:columns-4">
            {cats.map((c) => <CatBlock key={c.slug} c={c} lang={lang} close={() => setMenu(false)} />)}
          </div>
        </div>
      </nav>
    </header>
  );
};
